import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useState } from "react";
import { useParams, useHistory, Link, useLocation } from "react-router-dom";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import { getTileLogoDetails, updateTileLogo } from "../api/tileLogoApi";
import AssetSelector from "../../common/components/AssetSelector";
import styles from "./UpdateTileLogoPage.module.css";
import { validTileLogoFileSize } from "../validations/tileLogoValidations";
import { formatTime } from "./../../common/utils/generalUtils";
import { useUser } from "../../authentication/api/userApi";

function UpdateTileLogoPage() {
  const history = useHistory();
  const location = useLocation();

  // Context state
  const { user } = useContext(UserContext);

  // Input value states
  const [tileLogoName, setTileLogoName] = useState(null);
  const [tileLogoFile, setTileLogoFile] = useState(null);

  // Error states
  const [tileLogoNameError, setTileLogoNameError] = useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  // TileLogo State
  const [iconDetailsObj, setTileLogoDetailsObj] = useState(null);
  const updatedBy = useUser(iconDetailsObj?.last_updated_by);
  const createdBy = useUser(iconDetailsObj?.created_by);

  // Page loading state
  const [isLoading, setIsLoading] = useState(true);

  // Notification state
  const [notification, setNotification] = useState(null);

  // Params state
  const { id } = useParams();

  function buildNotification() {
    return {
      title: "Tile Logo updated",
      content: `Tile Logo for ${iconDetailsObj.combined_name} has been updated successfully`,
    };
  }

  const clearHistoryNotificationState = (history) => {
    let state = { ...history.location.state };
    delete state.notification;
    history.replace({
      ...history.location,
      state,
    });
  };

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    //Check if icon file is selected
    if (tileLogoFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("A file must be selected");
    }

    if (tileLogoFile) {
      // Check if icon file dimensions are the same as the icon size selected
      const isTileLogoFileSizeValid = await validTileLogoFileSize(tileLogoFile);

      if (isTileLogoFileSizeValid) {
        setIsLoading(true);
        try {
          await updateTileLogo(
            user.uid,
            id,
            tileLogoFile,
            iconDetailsObj.combined_name
          );
          setIsLoading(false);
          history.push(`/${routes.TILE_LOGO}/${routes.ASSETS}/${id}`, {
            notification: buildNotification(),
          });
        } catch (error) {
          setIsLoading(false);
          if (error.name === "FirebaseError") {
            console.log(error.message);
          } else if (
            error.message === "Tile Logo with the same name already exists"
          ) {
            setTileLogoNameError(error.message);
          }
        }
      } else {
        setIsLoading(false);
        setAssetSelectorError(`Dimensions of Tile Logo file must be 40 x 40`);
      }
    }
  };

  useEffect(() => {
    getTileLogoDetails(id)
      .then((doc) => {
        setTileLogoDetailsObj(doc);
        setTileLogoName(doc.combined_name);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
    if (location.state && location["state"].notification) {
      setNotification(location.state.notification);
    }
  }, [setTileLogoDetailsObj, setIsLoading, id, location]);

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <React.Fragment>
          <div className={`panel ${notification ? "show" : ""}`}>
            <div className={`animateFlag ${notification ? "visible" : ""}`}>
              <Flag
                success
                floating
                title={notification ? notification.title : ""}
                content={notification ? notification.content : ""}
                onDismiss={() => {
                  setNotification(null);
                  clearHistoryNotificationState(history);
                }}
              />
            </div>
          </div>
          <div className={styles.updatePageContainer}>
            <div className={styles.breadcrumbContainer}>
              <Breadcrumb>
                <Crumb
                  text="assets"
                  tagProps={{
                    to: `/${routes.TILE_LOGO}/${routes.ASSETS}`,
                  }}
                  as={Link}
                />
                <Separator />
                <Crumb
                  text={iconDetailsObj.combined_name}
                  tagProps={{
                    to: `/${routes.TILE_LOGO}/${routes.ASSETS}/${id}`,
                  }}
                  as={Link}
                />
              </Breadcrumb>
            </div>
            <div>
              <h3 className={styles.heading}>{iconDetailsObj.combined_name}</h3>
              <div style={{ marginBottom: '2rem' }}>
                  {/* First row */}
                  <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '20px',
                      width: '100%'
                  }}>
                      <div style={{ width: '45%' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Last updated at</div>
                          <div style={{ margin: '0' }}>{formatTime(iconDetailsObj.last_updated_at)}</div>
                      </div>
                      <div style={{ width: '45%' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Created at</div>
                          <div style={{ margin: '0' }}>{formatTime(iconDetailsObj.created_at)}</div>
                      </div>
                  </div>

                  {/* Second row */}
                  <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '20px',
                      width: '100%'
                  }}>
                      <div style={{ width: '45%' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Last updated by</div>
                          <div style={{ margin: '0' }}>{updatedBy ? `${updatedBy.name}` : "Loading..."}</div>
                      </div>
                      <div style={{ width: '45%' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Created by</div>
                          <div style={{ margin: '0' }}>{createdBy ? `${createdBy.name}` : "Loading..."}</div>
                      </div>
                  </div>

                                {/* Third row */}
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%'
                                  }}>
                                    <div style={{ width: '45%' }}>
                                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Aloha Version</div>
                                      <div style={{ margin: '0' }}>{iconDetailsObj.android?.aloha_version ?? "NA"}</div>
                                    </div>
                                    <div style={{ width: '45%' }}>
                                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Aloha Release Date</div>
                                      <div style={{ margin: '0' }}>
                                            {iconDetailsObj.android?.date
                                            ? formatTime(iconDetailsObj.android.date)
                                            : "NA"}
                                        </div>
                                    </div>
                                  </div>
              </div>
              <h5 className={styles.subHeading}>Edit Tile Logo</h5>
              <div className={styles.formRow}>
                <div className={styles.tileLogoNameContainer}>
                  <p className={styles.inputLabel}>Tile Logo Name</p>
                  <div className={styles.inputContainer}>
                    <TextField
                      required={!!tileLogoNameError}
                      size="m"
                      placeholder="Tile Logo Name"
                      type="text"
                      disabled={true}
                      stretch
                      onChange={(e) => {
                        setTileLogoName(e.target.value);
                        setTileLogoNameError(null);
                      }}
                      pattern="[a-z\d_]+"
                      value={tileLogoName}
                    />
                  </div>
                  {tileLogoNameError ? (
                    <Flag error title={tileLogoNameError} />
                  ) : null}
                </div>
              </div>
              <div className={styles.assetSelectorContainer}>
                <div className={styles.inputContainer}>
                  <AssetSelector
                    heading="Tile Logo"
                    asset={iconDetailsObj.img_url}
                    onAssetSelected={(file) => {
                      if (file) {
                        setTileLogoFile(file);
                        setAssetSelectorError(null);
                      }
                    }}
                  />
                </div>
                {assetSelectorError ? (
                  <Flag error title={assetSelectorError} />
                ) : null}
              </div>
              <Stack>
                <Button primary onClick={handleSaveClick}>
                  Save
                </Button>
                <Button secondary onClick={handeCancelClick}>
                  Cancel
                </Button>
              </Stack>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default UpdateTileLogoPage;
